import { Box, Container, Paper, Typography, Button as _LoadingButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { object, string, TypeOf } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginFormInput } from '../share';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useResetPasswordMutation } from '../apis/Auth.api';
import { ResetPasswordRequest } from '../types/auth.d';
import { useDynamicSnackbar } from '../share';
import {useTitle} from 'react-use';
import { Trans } from "@lingui/react/macro";
import { t, msg } from "@lingui/core/macro";

const LoadingButton = styled(_LoadingButton)`
  padding: 0.6rem 0;
  font-weight: 500;

  &:hover {
    transform: translateY(-2px);
  }
`;

const handleError = (actionError: any, openFailSnackbar) => {
    if (Array.isArray(actionError?.data?.error)) {
        actionError.data.error.forEach((el: any) => {
            openFailSnackbar(el.message)
        });
    } else if (actionError?.data?.message) {
        openFailSnackbar(actionError.data.message);
    } else {
        console.log('Unknown error', actionError)
        openFailSnackbar(actionError.data.message);
    }
}

const resetPasswordSchema = object({
    password: string()
        .trim()
        .min(1, msg`Password is required`)
        .min(8, msg`Password must be more than 8 characters`),
    passwordConfirm: string()
        .trim()
        .min(1, msg`Please confirm your password`),
}).refine((data) => data.password === data.passwordConfirm, {
    message: msg`Passwords do not match`,
    path: ['passwordConfirm'],
});

export type ResetPasswordPage = TypeOf<typeof resetPasswordSchema>;

const ResetPasswordPage = () => {

    useTitle(t`Reset Password`);
    
    const [searchParams, _] = useSearchParams();
    const resetToken = searchParams.get('token') ?? '';

    const methods = useForm<ResetPasswordRequest>({
        resolver: zodResolver(resetPasswordSchema),
    });

    const [resetPassword, { isLoading, isError, error, isSuccess, data }] =
        useResetPasswordMutation();

    const navigate = useNavigate();

    const {
        reset,
        handleSubmit,
        formState: { isSubmitSuccessful },
    } = methods;

    const {openSuccessSnackbar, openFailSnackbar} = useDynamicSnackbar();

    useEffect(() => {
        if (isSuccess) {
            navigate('/login');
            openSuccessSnackbar(t`Password updated successfully, login`);
        }
        if (isError) {
            handleError(error, openFailSnackbar)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitSuccessful]);

    const onSubmitHandler: SubmitHandler<ResetPasswordPage> = async (values) => {
        try {
            const resp = await resetPassword({ ...values, resetToken })
            console.log("resetPassword", resp)
        } catch (e) {
            handleError(e, openFailSnackbar)
        }
    };

    return (
      <Paper sx={ { padding: "2em" } }>
        <Container
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                }}
            >
                <Typography
                   
                    component='h2'
                    variant='h2' sx={{ textAlign: "center" }}>
                    <Trans>Reset Password</Trans>
                </Typography>
                <Typography
                    sx={{
                        fontSize: 15,
                        width: '100%',
                        textAlign: 'center',
                        mb: '1rem',
                    }}
                >
                    <Trans>Please enter a new password</Trans>
                </Typography>
                <FormProvider {...methods}>
                    <Box
                        component='form'
                        onSubmit={handleSubmit(onSubmitHandler)}
                        noValidate
                        autoComplete='off'
                        maxWidth='27rem'
                        width='100%'
                        sx={{
                            p: { xs: '1rem', sm: '2rem' },
                            borderRadius: 2,
                        }}
                    >
                        <LoginFormInput name='password' label={t`Password`} type='password' />
                        <LoginFormInput
                            name='passwordConfirm'
                            label={t`Confirm Password`}
                            type='password'
                        />

                        <LoadingButton
                            variant='contained'
                            sx={{ mt: 1 }}
                            fullWidth
                            disableElevation
                            type='submit'
                            loading={isLoading}
                        >
                            <Trans>Reset Password</Trans>
                        </LoadingButton>
                    </Box>
                </FormProvider>
            </Box>
        </Container>
      </Paper>
    );
};

export default ResetPasswordPage;
